import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { useMemo } from "react";

export type MediaFileActionCapabilities = {
  readonly isKeepAllowed: boolean;
  readonly isMakePublicAllowed: boolean;
  readonly isAddToFolderAllowed: boolean;
  readonly isReportAllowed: boolean;
  readonly isDeleteAllowed: boolean;
};

/**
 * The capabilities behind the media file actions menu, read once for whichever surface draws it.
 *
 * Download is deliberately absent: it is the one gated action already wired to a handler, so its container
 * gates it directly alongside the handler it passes down. Keep and Make private read their keys from the
 * capabilities sheet; neither has a handler yet, so the capability is the whole answer for now.
 *
 * Add to folder, Report and Delete have no key on the sheet, and none is invented here to stand in for one.
 * Until a row exists no brand can be allowed them, so the menu never offers them — the rows stay in the
 * shared components, ready for the milestone that adds each key and handler.
 */
export default function useMediaFileActionCapabilities(): MediaFileActionCapabilities {
  const isKeepAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.MediaKeep);
  const isMakePublicAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.MediaMakePublic);

  return useMemo(
    () => ({ isKeepAllowed, isMakePublicAllowed, isAddToFolderAllowed: false, isReportAllowed: false, isDeleteAllowed: false }),
    [isKeepAllowed, isMakePublicAllowed],
  );
}
